import { inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { LazyBusyStateStreamLoader } from './lazy-busy-state-stream-loader';

/**
 * Creates and registers a busy signal which is scoped to a component or a service.
 * When the component or service are destroyed, that busy signal is automatically
 * unregistered.
 *
 * Internally, this creates a behavior subject and registers it on the loading state service.
 *
 * Example usage:
 * ```ts
 * @Component({ ... })
 * export class SomeComponent extends Destroyable {
 *     private readonly busy = scopedBusySignal(this);
 *
 *     private someHeavyTask(): void {
 *         this.busy.on();
 *
 *         // Heavy implementation
 *
 *         this.busy.off();
 *     }
 * }
 * ```
 *
 * @param {{ destroy$: { subscribe: Function; }; }} scope The component or service which lifecycle's defines the registration and unregistration of the signal.
 * Extend the `Destroyable` class we have so you can pass `this` as the scope.
 * TODO(Angular 16+): Remove the scope param and inject `DestroyRef` in the function.
 *
 * @return {*} An object with API to toggle the busy signal.
 */
// Using implicit return type on purpose to let the implementation define the API.
export function scopedBusySignal(scope: { destroy$: { subscribe: Function } }) {
    const streamLoader = inject(LazyBusyStateStreamLoader);
    const busySignal = new BehaviorSubject<boolean>(false);

    streamLoader.registerLazyBusyStateStream(busySignal);
    scope.destroy$.subscribe(() => streamLoader.unregisterLazyBusyStateStream(busySignal));

    return {
        on: () => busySignal.next(true),
        off: () => busySignal.next(false)
    } as const;
}
